/**
 * Adapts `src/backend/app.ts`'s `GET /api/health` (`{ status: 'ok' }`, no
 * latency info) to `HealthView.vue`'s `HealthProvider` interface, which
 * needs a measured `latencyMs` — measured client-side around the request
 * (Story 015.3a, AC2 option A). `HealthView.vue` needs zero changes.
 */
import type { HealthProvider } from '../views/HealthView.vue';

export interface BackendHealthProviderConfig {
  baseUrl?: string;
}

export function makeBackendHealthProvider(
  config: BackendHealthProviderConfig,
  fetchImpl: typeof fetch = fetch,
): HealthProvider {
  const base = config.baseUrl ?? '';

  return {
    async healthCheck() {
      const start = performance.now();
      let res: Response;
      try {
        res = await fetchImpl(`${base}/api/health`);
      } catch {
        return { ok: false, latencyMs: performance.now() - start, message: 'Backend unavailable.' };
      }
      const latencyMs = performance.now() - start;

      if (!res.ok) {
        let message = 'Backend unavailable.';
        try {
          const body = (await res.json()) as { error?: string };
          if (body.error) message = body.error;
        } catch {
          // Malformed/non-JSON error body (e.g. an nginx/Ingress error page) — keep the generic message.
        }
        return { ok: false, latencyMs, message };
      }

      try {
        await res.json();
      } catch {
        // Malformed/non-JSON success body — the HTTP status is still authoritative.
      }
      return { ok: true, latencyMs };
    },
  };
}
